import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";

export function buildVisibilityConfirmTitle(nextIsVisible: boolean): string {
  const action = nextIsVisible ? "Show" : "Hide";
  const location = nextIsVisible ? "on" : "from";
  return `${action} profile ${location} the Marketplace?`;
}

function buildVisibilitySubject(profileName?: string): string {
  const normalizedName = profileName?.trim();
  if (!normalizedName || normalizedName.toLowerCase() === "this") {
    return "this profile";
  }
  return `${normalizedName}'s profile`;
}

export function buildVisibilityConfirmMessage(profileName: string, nextIsVisible: boolean): string {
  const subject = buildVisibilitySubject(profileName);
  if (nextIsVisible) {
    return `Show ${subject}? This profile will appear in searches on the Marketplace.`;
  }
  return `Hide ${subject}? This profile will no longer be searchable on the Marketplace.`;
}

export function buildVisibilitySuccessMessage(profileName: string, isVisible: boolean): string {
  const subject = buildVisibilitySubject(profileName);
  if (isVisible) {
    return `${subject} is now shown on the Marketplace`;
  }
  return `${subject} is now hidden from the Marketplace`;
}

export function buildVisibilityErrorMessage(nextIsVisible: boolean): string {
  if (nextIsVisible) {
    return "Couldn't show profile on Marketplace. Please try again";
  }
  return "Couldn't hide profile from Marketplace. Please try again";
}

const StyledVisibilitySwitch = styled(FormControlLabel)(() => ({
  whiteSpace: "nowrap",
}));

export type VisibilityToggleSwitchProps = {
  checked: boolean;
  disabled: boolean;
  label?: ReactNode;
  ariaLabel?: string;
  size?: "small" | "medium";
  stopPropagation?: boolean;
  onChange: (nextIsVisible: boolean) => void;
};

export default function VisibilityToggleSwitch(props: Readonly<VisibilityToggleSwitchProps>) {
  const { checked, disabled, label, ariaLabel, size = "medium", stopPropagation = false, onChange } = props;

  const control = (
    <Switch
      color="primary"
      size={size}
      checked={checked}
      disabled={disabled}
      slotProps={{
        input: {
          role: "switch",
          "aria-label": ariaLabel,
        },
      }}
      onClick={(event) => {
        if (stopPropagation) event.stopPropagation();
      }}
      onChange={(event) => {
        if (stopPropagation) event.stopPropagation();
        onChange(event.target.checked);
      }}
    />
  );

  if (label === undefined) {
    return control;
  }

  return (
    <StyledVisibilitySwitch
      label={label}
      labelPlacement="end"
      control={control}
    />
  );
}
